import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Edit2, Save, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function KRAKPIManager({ departments, selectedDept, setSelectedDept, tenantId }) {
  const [kras, setKras] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [showKRAForm, setShowKRAForm] = useState(false);
  const [showKPIForm, setShowKPIForm] = useState(false);
  const [editingKRA, setEditingKRA] = useState(null);
  const [editingKPI, setEditingKPI] = useState(null);
  const [selectedKRA, setSelectedKRA] = useState(null);
  const [kraForm, setKraForm] = useState({ title: '', description: '' });
  const [kpiForm, setKpiForm] = useState({ title: '', description: '', weightage: '', sop: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedDept) {
      fetchKRAsAndKPIs();
    }
  }, [selectedDept]);

  const fetchKRAsAndKPIs = async () => {
    try {
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';
      const [krasRes, kpisRes] = await Promise.all([
        axios.get(`${API}/kras/${selectedDept}${tenantParam}`),
        axios.get(`${API}/kpis/department/${selectedDept}${tenantParam}`)
      ]);
      setKras(krasRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error fetching KRAs/KPIs:', error);
      toast.error('Failed to load KRAs/KPIs');
    }
  };

  const handleCreateKRA = async () => {
    if (!kraForm.title.trim()) {
      toast.error('KRA title is required');
      return;
    }

    setLoading(true);
    try {
      if (editingKRA) {
        await axios.put(`${API}/kras/${editingKRA.id}`, {
          department_id: selectedDept,
          ...kraForm
        });
        toast.success('KRA updated successfully');
      } else {
        const tenantParam = tenantId ? `&tenant_id=${tenantId}` : '';
        await axios.post(`${API}/kras?tenant_id=${tenantId || ''}`, {
          department_id: selectedDept,
          ...kraForm
        });
        toast.success('KRA created successfully');
      }
      setKraForm({ title: '', description: '' });
      setShowKRAForm(false);
      setEditingKRA(null);
      fetchKRAsAndKPIs();
    } catch (error) {
      console.error('Error saving KRA:', error);
      toast.error('Failed to save KRA');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKPI = async () => {
    if (!kpiForm.title.trim() || !kpiForm.weightage) {
      toast.error('KPI title and weightage are required');
      return;
    }

    const weightage = parseFloat(kpiForm.weightage);
    if (isNaN(weightage) || weightage <= 0 || weightage > 100) {
      toast.error('Weightage must be between 0 and 100');
      return;
    }

    // Check total weightage
    const currentKPIs = editingKPI 
      ? kpis.filter(k => k.kra_id === selectedKRA && k.id !== editingKPI.id)
      : kpis.filter(k => k.kra_id === selectedKRA);
    const totalWeightage = currentKPIs.reduce((sum, kpi) => sum + kpi.weightage, 0) + weightage;
    
    if (totalWeightage > 100) {
      toast.error(`Total weightage cannot exceed 100%. Current: ${(totalWeightage - weightage).toFixed(1)}%`);
      return;
    }

    setLoading(true);
    try {
      if (editingKPI) {
        await axios.put(`${API}/kpis/${editingKPI.id}`, {
          kra_id: selectedKRA,
          department_id: selectedDept,
          ...kpiForm,
          weightage
        });
        toast.success('KPI updated successfully');
      } else {
        await axios.post(`${API}/kpis?tenant_id=${tenantId || ''}`, {
          kra_id: selectedKRA,
          department_id: selectedDept,
          ...kpiForm,
          weightage
        });
        toast.success('KPI created successfully');
      }
      setKpiForm({ title: '', description: '', weightage: '', sop: '' });
      setShowKPIForm(false);
      setEditingKPI(null);
      fetchKRAsAndKPIs();
    } catch (error) {
      console.error('Error saving KPI:', error);
      toast.error('Failed to save KPI');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKRA = async (kraId, isPredefined) => {
    if (isPredefined) {
      toast.error('Cannot delete predefined KRA. Only Super Admin can manage templates.');
      return;
    }
    
    if (!window.confirm('Are you sure? This will also delete all associated KPIs.')) return;

    try {
      await axios.delete(`${API}/kras/${kraId}`);
      toast.success('KRA deleted successfully');
      fetchKRAsAndKPIs();
    } catch (error) {
      console.error('Error deleting KRA:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete KRA';
      toast.error(errorMsg);
    }
  };

  const handleDeleteKPI = async (kpiId, isPredefined) => {
    if (isPredefined) {
      toast.error('Cannot delete predefined KPI. Only Super Admin can manage templates.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this KPI?')) return;

    try {
      await axios.delete(`${API}/kpis/${kpiId}`);
      toast.success('KPI deleted successfully');
      fetchKRAsAndKPIs();
    } catch (error) {
      console.error('Error deleting KPI:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete KPI';
      toast.error(errorMsg);
    }
  };

  const startEditKRA = (kra) => {
    if (kra.is_predefined) {
      toast.error('Cannot edit predefined KRA. Only Super Admin can manage templates.');
      return;
    }
    setEditingKRA(kra);
    setKraForm({ title: kra.title, description: kra.description });
    setShowKRAForm(true);
  };

  const startEditKPI = (kpi) => {
    if (kpi.is_predefined) {
      toast.error('Cannot edit predefined KPI. Only Super Admin can manage templates.');
      return;
    }
    setEditingKPI(kpi);
    setSelectedKRA(kpi.kra_id);
    setKpiForm({ 
      title: kpi.title, 
      description: kpi.description, 
      weightage: kpi.weightage.toString(),
      sop: kpi.sop || ''
    });
    setShowKPIForm(true);
  };

  const cancelForms = () => {
    setShowKRAForm(false);
    setShowKPIForm(false);
    setEditingKRA(null);
    setEditingKPI(null);
    setKraForm({ title: '', description: '' });
    setKpiForm({ title: '', description: '', weightage: '', sop: '' });
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-white shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Manage KRAs & KPIs</h2>
          <Select value={selectedDept || ''} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-64" data-testid="dept-select-kra">
              <SelectValue placeholder="Select Department" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!selectedDept ? (
          <p className="text-center text-slate-500 py-12">Select a department to manage KRAs and KPIs</p>
        ) : (
          <div className="space-y-6">
            {/* KRA Form */}
            {showKRAForm && (
              <Card className="p-6 bg-blue-50 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  {editingKRA ? 'Edit KRA' : 'Create New KRA'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="kra-title">KRA Title *</Label>
                    <Input
                      id="kra-title"
                      placeholder="e.g., Student Enrollment Growth"
                      value={kraForm.title}
                      onChange={(e) => setKraForm({ ...kraForm, title: e.target.value })}
                      data-testid="kra-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="kra-desc">Description</Label>
                    <Textarea
                      id="kra-desc"
                      placeholder="Describe the key result area..."
                      value={kraForm.description}
                      onChange={(e) => setKraForm({ ...kraForm, description: e.target.value })}
                      rows={3}
                      data-testid="kra-desc-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleCreateKRA} 
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="save-kra-button"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingKRA ? 'Update KRA' : 'Create KRA'}
                    </Button>
                    <Button variant="outline" onClick={cancelForms} data-testid="cancel-kra-button">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* KPI Form */}
            {showKPIForm && (
              <Card className="p-6 bg-purple-50 border-2 border-purple-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  {editingKPI ? 'Edit KPI' : 'Create New KPI'}
                </h3>
                <div className="space-y-4">
                  {!editingKPI && (
                    <div>
                      <Label htmlFor="kpi-kra">Select KRA *</Label>
                      <Select value={selectedKRA || ''} onValueChange={setSelectedKRA}>
                        <SelectTrigger data-testid="kpi-kra-select">
                          <SelectValue placeholder="Select KRA" />
                        </SelectTrigger>
                        <SelectContent>
                          {kras.map((kra) => (
                            <SelectItem key={kra.id} value={kra.id}>
                              {kra.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="kpi-title">KPI Title *</Label>
                    <Input
                      id="kpi-title"
                      placeholder="e.g., Monthly Enrollment Target Achievement"
                      value={kpiForm.title}
                      onChange={(e) => setKpiForm({ ...kpiForm, title: e.target.value })}
                      data-testid="kpi-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="kpi-desc">Description</Label>
                    <Textarea
                      id="kpi-desc"
                      placeholder="Describe the KPI..."
                      value={kpiForm.description}
                      onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })}
                      rows={2}
                      data-testid="kpi-desc-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="kpi-sop">Standard Operating Procedure (SOP)</Label>
                    <Textarea
                      id="kpi-sop"
                      placeholder="Define step-by-step procedures to achieve this KPI..."
                      value={kpiForm.sop}
                      onChange={(e) => setKpiForm({ ...kpiForm, sop: e.target.value })}
                      rows={4}
                      data-testid="kpi-sop-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Clear SOPs help department heads understand exactly how to achieve their goals</p>
                  </div>
                  <div>
                    <Label htmlFor="kpi-weight">Weightage (%) *</Label>
                    <Input
                      id="kpi-weight"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="e.g., 25"
                      value={kpiForm.weightage}
                      onChange={(e) => setKpiForm({ ...kpiForm, weightage: e.target.value })}
                      data-testid="kpi-weight-input"
                    />
                    {selectedKRA && (
                      <p className="text-sm text-slate-600 mt-1">
                        Current total: {kpis.filter(k => k.kra_id === selectedKRA).reduce((sum, k) => sum + k.weightage, 0).toFixed(1)}%
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleCreateKPI} 
                      disabled={loading || !selectedKRA}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="save-kpi-button"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingKPI ? 'Update KPI' : 'Create KPI'}
                    </Button>
                    <Button variant="outline" onClick={cancelForms} data-testid="cancel-kpi-button">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Action Buttons */}
            {!showKRAForm && !showKPIForm && (
              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowKRAForm(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-kra-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add KRA
                </Button>
                <Button 
                  onClick={() => setShowKPIForm(true)}
                  disabled={kras.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="add-kpi-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add KPI
                </Button>
              </div>
            )}

            {/* KRAs and KPIs Display */}
            <div className="space-y-6 mt-8">
              {kras.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No KRAs defined yet. Create one to get started.</p>
              ) : (
                kras.map((kra) => {
                  const kraKpis = kpis.filter((kpi) => kpi.kra_id === kra.id);
                  const totalWeightage = kraKpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
                  const isKraPredefined = kra.is_predefined || false;
                  
                  return (
                    <Card key={kra.id} className={`p-6 border-2 ${isKraPredefined ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200'}`} data-testid="kra-card-manager">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-bold text-slate-900">{kra.title}</h3>
                            {isKraPredefined && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-indigo-100 rounded-full">
                                <Lock className="w-3 h-3 text-indigo-600" />
                                <span className="text-xs font-medium text-indigo-700">Predefined</span>
                              </div>
                            )}
                          </div>
                          {kra.description && (
                            <p className="text-slate-600 mt-1">{kra.description}</p>
                          )}
                        </div>
                        {!isKraPredefined && (
                          <div className="flex gap-2 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditKRA(kra)}
                              data-testid="edit-kra-button"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteKRA(kra.id, isKraPredefined)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid="delete-kra-button"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {kraKpis.length > 0 ? (
                        <div className="space-y-2 mt-4">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm font-semibold text-slate-700">KPIs:</p>
                            <p className="text-sm text-slate-600">
                              Total Weightage: <span className={`font-bold ${totalWeightage === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                                {totalWeightage.toFixed(1)}%
                              </span>
                            </p>
                          </div>
                          {kraKpis.map((kpi) => {
                            const isKpiPredefined = kpi.is_predefined || false;
                            return (
                            <div key={kpi.id} className={`flex justify-between items-center p-4 rounded-lg ${isKpiPredefined ? 'bg-indigo-50/50 border-2 border-indigo-200' : 'bg-slate-50'}`} data-testid="kpi-item-manager">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-slate-900">{kpi.title}</p>
                                  {isKpiPredefined && (
                                    <Lock className="w-3 h-3 text-indigo-600" title="Predefined by Super Admin" />
                                  )}
                                </div>
                                {kpi.description && (
                                  <p className="text-sm text-slate-600 mt-1">{kpi.description}</p>
                                )}
                                {kpi.sop && (
                                  <div className="mt-2 p-3 bg-blue-50 border-l-4 border-l-blue-500 rounded">
                                    <p className="text-xs font-semibold text-blue-900 mb-1">SOP:</p>
                                    <p className="text-xs text-slate-700 whitespace-pre-wrap">{kpi.sop}</p>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-3 ml-4">
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg font-bold text-sm">
                                  {kpi.weightage}%
                                </span>
                                {!isKpiPredefined && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => startEditKPI(kpi)}
                                      data-testid="edit-kpi-button"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteKPI(kpi.id, isKpiPredefined)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      data-testid="delete-kpi-button"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm mt-2">No KPIs defined for this KRA</p>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}